import type { DeviceCategory } from '@/api/specifications/specifications.types';

/**
 * Frontend mirror of the backend's `domain/category-attributes.ts`. The
 * authoritative source is the backend — when a new category or attribute is
 * added there, this file must be updated to match. Both files keep their own
 * copies to avoid a build-time dependency between backend and frontend.
 */
export type AttributeClass =
  | 'mechanical-numeric'
  | 'mechanical-closed-enum'
  | 'semantic-governed';

export interface AttributeSpec {
  key: string;
  class: AttributeClass;
  enumValues?: readonly string[];
}

export const ATTRIBUTE_SPECS_BY_CATEGORY: Record<DeviceCategory, AttributeSpec[]> = {
  smartphone: [
    { key: 'storage', class: 'mechanical-numeric' },
    { key: 'ram', class: 'mechanical-numeric' },
    { key: 'year', class: 'mechanical-numeric' },
    {
      key: 'connectivity',
      class: 'mechanical-closed-enum',
      enumValues: ['5G', 'LTE', '4G'],
    },
    {
      key: 'region',
      class: 'mechanical-closed-enum',
      enumValues: ['DE', 'EU', 'DACH', 'US', 'AT', 'CH'],
    },
    {
      key: 'condition',
      class: 'mechanical-closed-enum',
      enumValues: ['B-Ware'],
    },
    { key: 'color', class: 'semantic-governed' },
  ],
  tablet: [
    { key: 'storage', class: 'mechanical-numeric' },
    { key: 'ram', class: 'mechanical-numeric' },
    { key: 'screenSize', class: 'mechanical-numeric' },
    { key: 'year', class: 'mechanical-numeric' },
    {
      key: 'connectivity',
      class: 'mechanical-closed-enum',
      enumValues: ['5G', 'LTE', '4G', 'WIFI', 'CELLULAR'],
    },
    {
      key: 'region',
      class: 'mechanical-closed-enum',
      enumValues: ['DE', 'EU', 'DACH', 'US', 'AT', 'CH'],
    },
    {
      key: 'condition',
      class: 'mechanical-closed-enum',
      enumValues: ['B-Ware'],
    },
    { key: 'chip', class: 'semantic-governed' },
    { key: 'color', class: 'semantic-governed' },
  ],
  smartwatch: [
    { key: 'caseSize', class: 'mechanical-numeric' },
    { key: 'year', class: 'mechanical-numeric' },
    {
      key: 'connectivity',
      class: 'mechanical-closed-enum',
      enumValues: ['GPS', 'CELLULAR'],
    },
    {
      key: 'condition',
      class: 'mechanical-closed-enum',
      enumValues: ['B-Ware'],
    },
    {
      key: 'bandSize',
      class: 'mechanical-closed-enum',
      enumValues: ['S/M', 'M/L', 'S', 'M', 'L', 'XL'],
    },
    { key: 'caseColor', class: 'semantic-governed' },
    { key: 'caseMaterial', class: 'semantic-governed' },
    { key: 'band', class: 'semantic-governed' },
  ],
  notebook: [
    { key: 'storage', class: 'mechanical-numeric' },
    { key: 'ram', class: 'mechanical-numeric' },
    { key: 'screenSize', class: 'mechanical-numeric' },
    { key: 'year', class: 'mechanical-numeric' },
    { key: 'cpuCores', class: 'mechanical-numeric' },
    { key: 'gpuCores', class: 'mechanical-numeric' },
    {
      key: 'keyboardLayout',
      class: 'mechanical-closed-enum',
      enumValues: ['DE', 'US', 'UK', 'FR'],
    },
    {
      key: 'condition',
      class: 'mechanical-closed-enum',
      enumValues: ['B-Ware'],
    },
    { key: 'chip', class: 'semantic-governed' },
    { key: 'color', class: 'semantic-governed' },
  ],
  desktop: [
    { key: 'storage', class: 'mechanical-numeric' },
    { key: 'ram', class: 'mechanical-numeric' },
    { key: 'screenSize', class: 'mechanical-numeric' },
    { key: 'year', class: 'mechanical-numeric' },
    { key: 'cpuCores', class: 'mechanical-numeric' },
    { key: 'gpuCores', class: 'mechanical-numeric' },
    {
      key: 'condition',
      class: 'mechanical-closed-enum',
      enumValues: ['B-Ware'],
    },
    { key: 'chip', class: 'semantic-governed' },
    { key: 'color', class: 'semantic-governed' },
  ],
};
